import React, { useEffect, useMemo, useState } from 'react';
import { doc, serverTimestamp, setDoc } from 'firebase/firestore';
import { Check, X } from 'lucide-react';
import { db } from '../../firebase';
import {
  GlassAccentButton,
  GlassAvatar,
  GLASS_MODAL_OVERLAY,
  GLASS_MODAL_PANEL,
  GLASS_SECONDARY_BUTTON,
} from '../glass/GlassUi';
import { effectiveReportAccess, isReportAdminRole } from '../../utils/reportCatalog';

export const staffName = (user) => (
  `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email || 'Unnamed user'
);

/**
 * Who can open a report. Everyone sees the list; Owners/Executives can switch on
 * "Everyone on the team" or pick people, saved to reportAccess/{reportId}.
 */
const ReportAccessModal = ({ report, accessDoc, users, canEdit, currentUid, onClose }) => {
  const initial = useMemo(() => effectiveReportAccess(report, accessDoc), [report, accessDoc]);
  const [everyone, setEveryone] = useState(initial.everyone);
  const [selected, setSelected] = useState(() => new Set(initial.userIds));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Owners and Executives first, then everyone else by name
  const sortedUsers = useMemo(() => [...users].sort((a, b) => {
    const adminDiff = Number(isReportAdminRole(b.role)) - Number(isReportAdminRole(a.role));
    return adminDiff || staffName(a).localeCompare(staffName(b));
  }), [users]);

  const hasAccess = (user) => isReportAdminRole(user.role) || everyone || selected.has(user.id);
  const listed = canEdit ? sortedUsers : sortedUsers.filter(hasAccess);
  const accessCount = sortedUsers.filter(hasAccess).length;

  const toggleUser = (uid) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(uid)) next.delete(uid); else next.add(uid);
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      // Only non-admin staff are stored; Owners/Executives always have access
      const userIds = sortedUsers
        .filter((u) => !isReportAdminRole(u.role) && selected.has(u.id))
        .map((u) => u.id);
      await setDoc(doc(db, 'reportAccess', report.id), {
        everyone,
        userIds,
        updatedAt: serverTimestamp(),
        updatedBy: currentUid || null,
      });
      onClose();
    } catch (e) {
      console.error('Error saving report access:', e);
      setError('Could not save access. Please try again.');
      setSaving(false);
    }
  };

  return (
    <div className={GLASS_MODAL_OVERLAY} onClick={onClose}>
      <div className={GLASS_MODAL_PANEL} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="report-access-title">
        <button
          type="button"
          onClick={onClose}
          className="absolute right-4 top-4 grid h-8 w-8 place-items-center rounded-full text-gray-400 transition hover:bg-black/5 hover:text-gray-700 dark:hover:bg-white/10 dark:hover:text-white"
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="mb-5 text-center">
          <div className="mb-2 text-3xl leading-none" role="img" aria-hidden="true">{report.emoji}</div>
          <h2 id="report-access-title" className="text-lg font-semibold tracking-tight">{report.title}</h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-white/[0.45]">
            {accessCount} {accessCount === 1 ? 'person has' : 'people have'} access
          </p>
        </div>

        {canEdit && (
          <label className="mb-3 flex cursor-pointer items-center justify-between gap-3 rounded-2xl border border-black/[0.06] bg-black/[0.03] px-4 py-3 dark:border-white/[0.06] dark:bg-white/[0.035]">
            <span>
              <span className="block text-sm font-medium">Everyone on the team</span>
              <span className="block text-xs text-gray-500 dark:text-white/[0.45]">Includes new staff as they join</span>
            </span>
            <input
              type="checkbox"
              checked={everyone}
              onChange={(e) => setEveryone(e.target.checked)}
              className="h-4 w-4 accent-[#F80707]"
            />
          </label>
        )}

        <ul className="divide-y divide-black/[0.06] dark:divide-white/[0.06]">
          {listed.map((user) => {
            const admin = isReportAdminRole(user.role);
            return (
              <li key={user.id} className="flex items-center gap-3 py-2.5">
                <GlassAvatar name={staffName(user)} />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium">{staffName(user)}</div>
                  <div className="truncate text-xs text-gray-500 dark:text-white/[0.45]">{user.role || 'Member'}{user.team ? ` · ${user.team}` : ''}</div>
                </div>
                {canEdit && (admin || everyone ? (
                  <span className="inline-flex items-center gap-1 text-xs text-gray-400 dark:text-white/40">
                    <Check className="h-3.5 w-3.5" />
                    {admin ? 'Always' : 'Everyone'}
                  </span>
                ) : (
                  <input
                    type="checkbox"
                    checked={selected.has(user.id)}
                    onChange={() => toggleUser(user.id)}
                    className="h-4 w-4 accent-[#F80707]"
                    aria-label={`Give ${staffName(user)} access`}
                  />
                ))}
              </li>
            );
          })}
        </ul>

        {error && <p className="mt-3 text-sm text-[#F80707]">{error}</p>}

        <div className="mt-6 flex justify-end gap-2">
          {canEdit ? (
            <>
              <button type="button" onClick={onClose} className={GLASS_SECONDARY_BUTTON}>Cancel</button>
              <GlassAccentButton onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save access'}</GlassAccentButton>
            </>
          ) : (
            <button type="button" onClick={onClose} className={GLASS_SECONDARY_BUTTON}>Close</button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReportAccessModal;
